import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { AdminService } from 'src/app/admin/admin.service';
import { SnackbarService } from 'src/app/snackbarService/snackbar.service';
import { UtilService } from 'src/app/shared/utils.service';

export interface TransporterSelectionData {
  tspPartner: string;
  tspPartnerName: string;
}

export interface TransporterSelectionModalConfig {
  existingTransporters?: TransporterSelectionData[];
}

interface Transporter {
  id: string | number;
  name: string;
  [key: string]: any;
}

@Component({
  selector: 'app-transporter-selection-modal',
  templateUrl: './transporter-selection-modal.component.html',
  styleUrls: ['./transporter-selection-modal.component.scss'],
})
export class TransporterSelectionModalComponent implements OnInit {
  /** When true, hides dialog chrome; parent owns footer. Use inside a host that provides MatDialogRef null. */
  @Input() embedded = false;
  @Input() existingTransporters: TransporterSelectionData[] = [];

  allTransporters: Transporter[] = [];
  selectedTransporters: Transporter[] = [];
  searchLeft = '';
  searchRight = '';
  isLoading = false;
  isUploading = false;

  constructor(
    @Optional() public dialogRef: MatDialogRef<TransporterSelectionModalComponent, any[] | undefined> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: TransporterSelectionModalConfig | null,
    private adminService: AdminService,
    private utilService: UtilService,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.loadTransporters();
  }

  get filteredAllTransporters(): Transporter[] {
    const q = this.searchLeft.trim().toLowerCase();
    return this.allTransporters.filter(
      (t) =>
        !this.selectedTransporters.some((st) => String(st.id) === String(t.id)) &&
        (t.name || '').toLowerCase().includes(q),
    );
  }

  get filteredSelectedTransporters(): Transporter[] {
    const q = this.searchRight.trim().toLowerCase();
    return this.selectedTransporters.filter((t) => (t.name || '').toLowerCase().includes(q));
  }

  loadTransporters(): void {
    this.isLoading = true;
    const req = this.adminService.getTransportersLiteSearch(5000, 1, { isApproved: [true] });
    if (!req) {
      this.isLoading = false;
      return;
    }
    req.subscribe({
      next: (result: any) => {
        const transporters = Array.isArray(result?.transporters) ? result.transporters : [];
        const mapped: Transporter[] = transporters.map((t: any) => ({
          id: t.id,
          name: t.companyName || String(t.id ?? ''),
          ...t,
        }));
        this.allTransporters = mapped;
        this.mergeExistingIntoSelection(mapped);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.snackbar.showErrorSnackBar('Failed to load transporters.', 'X');
      },
    });
  }

  private mergeExistingIntoSelection(mapped: Transporter[]): void {
    const existing = this.existingTransporters?.length
      ? this.existingTransporters
      : (this.data?.existingTransporters ?? []);
    if (!existing.length) {
      this.selectedTransporters = [];
      return;
    }
    this.selectedTransporters = existing.map((t) => {
      const matched = mapped.find((m) => String(m.id) === String(t.tspPartner));
      if (matched) {
        return matched;
      }
      return { id: t.tspPartner, name: t.tspPartnerName };
    });
  }

  addTransporter(transporter: Transporter): void {
    if (!this.selectedTransporters.some((st) => String(st.id) === String(transporter.id))) {
      this.selectedTransporters = [...this.selectedTransporters, transporter];
    }
  }

  removeTransporter(transporterId: string | number): void {
    this.selectedTransporters = this.selectedTransporters.filter((t) => t.id !== transporterId);
  }

  selectAllFiltered(): void {
    const next = [...this.selectedTransporters];
    this.filteredAllTransporters.forEach((transporter) => {
      if (!next.some((st) => String(st.id) === String(transporter.id))) {
        next.push(transporter);
      }
    });
    this.selectedTransporters = next;
  }

  clearAllSelected(): void {
    this.selectedTransporters = [];
  }

  handleSave(): void {
    const payload = this.selectedTransporters.slice();
    this.dialogRef?.close(payload);
  }

  handleClose(): void {
    this.searchLeft = '';
    this.searchRight = '';
    this.dialogRef?.close();
  }

  getSelectedCount(): number {
    return this.selectedTransporters.length;
  }

  handleExportTransporters(): void {
    try {
      if (this.allTransporters.length === 0) {
        this.snackbar.showErrorSnackBar('No transporters available to export', 'X');
        return;
      }
      const headers = ['TSP Partner ID', 'TSP Partner Name'];
      const rows = this.allTransporters.map((transporter) => [String(transporter.id), transporter.name]);
      const workbook = XLSX.utils.book_new();
      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transporters');
      const fileName = `transporters_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      this.snackbar.showSuccessSnackBar(
        `Successfully exported ${this.allTransporters.length} transporter(s) to Excel`,
        'X',
      );
    } catch (error: any) {
      this.snackbar.showErrorSnackBar(error?.message || 'Failed to export transporters. Please try again.', 'X');
    }
  }

  handleUploadClick(fileInput: HTMLInputElement): void {
    fileInput?.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!this.isExcelFile(file)) {
      this.snackbar.showErrorSnackBar('Please upload a valid Excel file (.xls, .xlsx, .xlsm, or .csv)', 'X');
      input.value = '';
      return;
    }
    try {
      this.isUploading = true;
      const result: any = await this.utilService.formatExcelData(event);
      const expectedHeaders = ['TSP Partner ID', 'TSP Partner Name'];
      const headers = (result.headers || []).map((h: string) => String(h).trim());
      const headerMap: Record<string, string> = {};
      expectedHeaders.forEach((expectedHeader) => {
        const found = headers.find((h) => h.toLowerCase() === expectedHeader.toLowerCase());
        if (found) {
          headerMap[expectedHeader] = found;
        }
      });
      const missingHeaders = expectedHeaders.filter((h) => !headerMap[h]);
      if (missingHeaders.length > 0) {
        this.isUploading = false;
        this.snackbar.showErrorSnackBar(`Missing required columns: ${missingHeaders.join(', ')}`, 'X');
        input.value = '';
        return;
      }
      const uploadedTransporters: Transporter[] = [];
      const errors: string[] = [];
      const idKey = headerMap['TSP Partner ID'];
      const nameKey = headerMap['TSP Partner Name'];
      (result.dataArray || []).forEach((row: any, index: number) => {
        const tspPartnerId = String(row[idKey] ?? '').trim();
        const tspPartnerName = String(row[nameKey] ?? '').trim();
        if (!tspPartnerId || !tspPartnerName) {
          errors.push(`Row ${index + 2}: Missing required fields`);
          return;
        }
        const existingTransporter = this.allTransporters.find((t) => String(t.id) === String(tspPartnerId));
        if (existingTransporter) {
          uploadedTransporters.push(existingTransporter);
        } else {
          uploadedTransporters.push({ id: tspPartnerId, name: tspPartnerName });
        }
      });
      if (uploadedTransporters.length === 0) {
        this.isUploading = false;
        this.snackbar.showErrorSnackBar('No valid transporters found in the uploaded file', 'X');
        input.value = '';
        return;
      }
      const newSelected = [...this.selectedTransporters];
      uploadedTransporters.forEach((transporter) => {
        if (!newSelected.some((st) => String(st.id) === String(transporter.id))) {
          newSelected.push(transporter);
        }
      });
      this.selectedTransporters = newSelected;
      if (errors.length > 0) {
        this.snackbar.showErrorSnackBar(
          `Uploaded ${uploadedTransporters.length} transporter(s) with ${errors.length} error(s).`,
          'X',
        );
      } else {
        this.snackbar.showSuccessSnackBar(
          `Successfully uploaded ${uploadedTransporters.length} transporter(s)`,
          'X',
        );
      }
    } catch (error: any) {
      this.snackbar.showErrorSnackBar(error?.message || 'Failed to parse Excel file. Please check the file format.', 'X');
    } finally {
      this.isUploading = false;
      input.value = '';
    }
  }

  private isExcelFile(file: File): boolean {
    const lower = file.name.toLowerCase();
    return ['.xls', '.xlsx', '.xlsm', '.csv'].some((ext) => lower.endsWith(ext));
  }
}
